import { useMemo, useState } from "react";

import { useLocalization } from "@fluent/react";
import {
  Button,
  Checkbox,
  Chip,
  Popover,
  Row,
  TextInput,
} from "@narmi/design_system";
import { groupAccountsByOrganization } from "byzantine";

import { useCurrentUser } from "../../../../../components/contexts/CurrentUserContext";
import { useIsMultiEntity } from "../../../liveLedger/hooks/useIsMultiEntity";

import styles from "./AccountFilterPopover.module.scss";

import type Account from "byzantine/src/Account";

interface AccountFilterPopoverProps {
  accounts: Account[];
  selectedAccountIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
}

/** Format account label as "Name-Number" (e.g. "Collections-5587") */
const getAccountLabel = (account: Account): string => {
  const name = account.getName();
  const maskedNumber = account.getMaskedNumber();
  return maskedNumber ? `${name}-${maskedNumber}` : name;
};

const AccountFilterPopover = ({
  accounts,
  selectedAccountIds,
  onSelectionChange,
}: AccountFilterPopoverProps) => {
  const { l10n } = useLocalization();
  const { currentUser } = useCurrentUser();
  const isMultiEntity = useIsMultiEntity(accounts);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // Local draft state — only committed to parent on Apply
  const [draft, setDraft] = useState<Set<string>>(
    () => new Set(selectedAccountIds)
  );

  const filteredAccounts = useMemo(() => {
    if (!searchQuery.trim()) return accounts;
    const query = searchQuery.trim().toLowerCase();
    return accounts.filter((account) =>
      getAccountLabel(account).toLowerCase().includes(query)
    );
  }, [accounts, searchQuery]);

  const organizationAccounts = useMemo(
    () => groupAccountsByOrganization(accounts, currentUser?.org_name),
    [accounts, currentUser?.org_name]
  );

  const filteredGroups = useMemo(
    () =>
      isMultiEntity
        ? Array.from(
            groupAccountsByOrganization(filteredAccounts, currentUser?.org_name)
          )
        : [],
    [isMultiEntity, filteredAccounts, currentUser?.org_name]
  );

  const handleToggleOrganization = (orgName: string) => {
    const orgAccountIds = (organizationAccounts.get(orgName) ?? []).map(
      (a) => a.id
    );
    setDraft((prev) => {
      const next = new Set(prev);
      const allChecked = orgAccountIds.every((id) => next.has(id));
      orgAccountIds.forEach((id) => {
        if (allChecked) {
          next.delete(id);
        } else {
          next.add(id);
        }
      });
      return next;
    });
  };

  const handleToggleAccount = (accountId: string) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) {
        next.delete(accountId);
      } else {
        next.add(accountId);
      }
      return next;
    });
  };

  const handleApply = () => {
    onSelectionChange([...draft]);
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleRemoveFilter = () => {
    onSelectionChange([]);
    setDraft(new Set());
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleOpen = () => {
    // Reset draft to current committed state when opening
    setDraft(new Set(selectedAccountIds));
    setSearchQuery("");
    setIsOpen(true);
  };

  const handleDismiss = () => {
    setIsOpen(false);
    setSearchQuery("");
  };

  const accountIdSet = new Set<string>(accounts.map((a) => a.id));
  const validSelectedCount = selectedAccountIds.filter((id) =>
    accountIdSet.has(id)
  ).length;
  const allSelected =
    accounts.length > 0 && validSelectedCount === accounts.length;
  // Before accounts load we can't validate ids, so fall back to the raw
  // selection count rather than showing a misleading "0".
  const selectedCount =
    accounts.length === 0 ? selectedAccountIds.length : validSelectedCount;

  const chipLabel = l10n.getString("transaction-export-account-filter-label");

  // Keeps focus inside the NDS Popover's FocusLock on click, or it scrolls back to the last focused checkbox.
  const popoverContent = (
    <div className={styles["account-filter-popover"]} tabIndex={-1}>
      <div className={styles["account-filter-popover-search"]}>
        <TextInput
          label={l10n.getString("transaction-export-account-filter-search")}
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchQuery(e.target.value)
          }
          testId="account-filter-search"
        />
      </div>
      {filteredAccounts.length > 0 && (
        <>
          <div className={styles["account-filter-popover-divider"]} />
          <div className={styles["account-filter-popover-select-all"]}>
            <Checkbox
              label={l10n.getString(
                "transaction-export-account-filter-select-all"
              )}
              kind="condensed"
              checked={filteredAccounts.every((a) => draft.has(a.id))}
              onChange={() => {
                const allFilteredSelected = filteredAccounts.every((a) =>
                  draft.has(a.id)
                );
                setDraft((prev) => {
                  const next = new Set(prev);
                  filteredAccounts.forEach((a) => {
                    if (allFilteredSelected) {
                      next.delete(a.id);
                    } else {
                      next.add(a.id);
                    }
                  });
                  return next;
                });
              }}
              testId="account-filter-select-all"
            />
          </div>
        </>
      )}
      <div className={styles["account-filter-popover-divider"]} />
      <div className={styles["account-filter-popover-list"]}>
        {isMultiEntity
          ? filteredGroups.map(([orgName, orgFilteredAccounts]) => {
              const orgAccountIds = (
                organizationAccounts.get(orgName) ?? []
              ).map((a) => a.id);
              const checkedCount = orgAccountIds.filter((id) =>
                draft.has(id)
              ).length;
              return (
                <div
                  key={orgName}
                  className={styles["account-filter-popover-group"]}
                >
                  <div
                    className={styles["account-filter-popover-group-header"]}
                  >
                    <Checkbox
                      label={orgName}
                      kind="condensed"
                      checked={checkedCount === orgAccountIds.length}
                      indeterminate={
                        checkedCount > 0 && checkedCount < orgAccountIds.length
                      }
                      onChange={() => handleToggleOrganization(orgName)}
                    />
                  </div>
                  {orgFilteredAccounts.map((account) => (
                    <div
                      key={account.id}
                      className={styles["account-filter-popover-nested"]}
                    >
                      <Checkbox
                        label={getAccountLabel(account)}
                        kind="condensed"
                        checked={draft.has(account.id)}
                        onChange={() => handleToggleAccount(account.id)}
                      />
                    </div>
                  ))}
                </div>
              );
            })
          : filteredAccounts.map((account) => (
              <Checkbox
                key={account.id}
                label={getAccountLabel(account)}
                kind="condensed"
                checked={draft.has(account.id)}
                onChange={() => handleToggleAccount(account.id)}
              />
            ))}
      </div>
      <div className={styles["account-filter-popover-divider"]} />
      <div className="padding--x--s padding--y--xs">
        <Row justifyContent="space-between" alignItems="center">
          <Button
            kind="plain"
            size="xs"
            label={l10n.getString("transaction-export-account-filter-remove")}
            onClick={handleRemoveFilter}
            type="button"
            testId="account-filter-remove"
          />
          <Button
            kind="primary"
            size="xs"
            label={l10n.getString("transaction-export-account-filter-apply")}
            onClick={handleApply}
            testId="account-filter-apply"
          />
        </Row>
      </div>
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      isOpen={isOpen}
      onUserDismiss={handleDismiss}
      testId="account-filter-popover"
    >
      <Chip
        kind="secondary"
        label={chipLabel}
        startIcon="bank"
        count={
          allSelected
            ? l10n.getString("transaction-export-filter-count-all")
            : String(selectedCount)
        }
        onClick={handleOpen}
        endIcon={`chevron-${isOpen ? "up" : "down"}`}
      />
    </Popover>
  );
};

export default AccountFilterPopover;
